// services/childcare/payments.js
import { api } from "./utils";
import { showLoading, updateToSuccess, updateToError } from "../../utils/toast";

// Get all payments
export const getPayments = async (filters = {}) => {
  try {
    const response = await api.get("/childcare/payments", { params: filters });
    return response;
  } catch (error) {
    console.error("Error fetching payments:", error);
    throw error;
  }
};

// Get payments by child
export const getPaymentsByChild = async (childId) => {
  try {
    const response = await api.get(`/childcare/payments/child/${childId}`);
    return response;
  } catch (error) {
    console.error("Error fetching child payments:", error);
    throw error;
  }
};

// Get payment by invoice number
export const getPaymentByInvoice = async (invoiceNumber) => {
  try {
    const response = await api.get(
      `/childcare/payments/invoice/${invoiceNumber}`,
    );
    return response;
  } catch (error) {
    console.error("Error fetching payment:", error);
    throw error;
  }
};

// Create payment
export const createPayment = async (paymentData) => {
  const toastId = showLoading("Creating payment...");
  try {
    const response = await api.post("/childcare/payments", paymentData);
    updateToSuccess(
      toastId,
      response.data?.message || "Payment created successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to create payment";
    updateToError(toastId, errorMessage);
    throw error;
  }
};

// Update payment
export const updatePayment = async (id, paymentData) => {
  const toastId = showLoading("Updating payment...");
  try {
    const response = await api.put(`/childcare/payments/${id}`, paymentData);
    updateToSuccess(
      toastId,
      response.data?.message || "Payment updated successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to update payment";
    updateToError(toastId, errorMessage);
    throw error;
  }
};

// Delete payment
export const deletePayment = async (id) => {
  const toastId = showLoading("Deleting payment...");
  try {
    const response = await api.delete(`/childcare/payments/${id}`);
    updateToSuccess(
      toastId,
      response.data?.message || "Payment deleted successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to delete payment";
    updateToError(toastId, errorMessage);
    throw error;
  }
};

// Process payment
export const processPayment = async (id, paymentDetails) => {
  try {
    const response = await api.post(
      `/childcare/payments/${id}/process`,
      paymentDetails,
    );
    return response;
  } catch (error) {
    console.error("Error processing payment:", error);
    throw error;
  }
};

// Refund payment
export const refundPayment = async (id, refundData) => {
  const toastId = showLoading("Processing refund...");
  try {
    const response = await api.post(
      `/childcare/payments/${id}/refund`,
      refundData,
    );
    updateToSuccess(
      toastId,
      response.data?.message || "Refund processed successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to process refund";
    updateToError(toastId, errorMessage);
    throw error;
  }
};

// Download invoice
export const downloadInvoice = async (id) => {
  try {
    const response = await api.get(`/childcare/invoices/${id}/download`, {
      responseType: "blob",
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `invoice_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  } catch (error) {
    console.error("Error downloading invoice:", error);
    throw error;
  }
};

// Get fee structures
export const getFeeStructures = async () => {
  try {
    const response = await api.get("/childcare/fee-structures");
    return response;
  } catch (error) {
    console.error("Error fetching fee structures:", error);
    throw error;
  }
};

// Create fee structure
export const createFeeStructure = async (feeData) => {
  const toastId = showLoading("Creating fee structure...");
  try {
    const response = await api.post("/childcare/fee-structures", feeData);
    updateToSuccess(
      toastId,
      response.data?.message || "Fee structure created successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to create fee structure";
    updateToError(toastId, errorMessage);
    throw error;
  }
};

// Update fee structure
export const updateFeeStructure = async (id, feeData) => {
  const toastId = showLoading("Updating fee structure...");
  try {
    const response = await api.put(`/childcare/fee-structures/${id}`, feeData);
    updateToSuccess(
      toastId,
      response.data?.message || "Fee structure updated successfully!",
    );
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to update fee structure";
    updateToError(toastId, errorMessage);
    throw error;
  }
};
